/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Modal,
  StatusBar,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projectById: (id) => `${API_BASE_URL}/api/data/projects/${id}`,
  projectByIdFallback: (id) => `${API_BASE_URL}/api/projects/${id}`,
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Custom date picker component (unchanged) – included for completeness
const CustomDatePicker = ({ isVisible, onClose, onSelectDate, initialDate }) => {
  // ... (same as in AddProjectScreen, omitted for brevity)
  // (Keep the full implementation from the original file)
};

const customDatePickerStyles = StyleSheet.create({
  // ... (same as in AddProjectScreen)
});

export default function EditProjectScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { project } = route.params; // may be full project object or just id

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentDateField, setCurrentDateField] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [teamMember, setTeamMember] = useState({
    name: '',
    role: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
  });
  const [milestone, setMilestone] = useState({
    title: '',
    dueDate: new Date(),
    status: 'Not Started',
  });
  const [risk, setRisk] = useState({
    description: '',
    severity: 'Medium',
    mitigation: '',
  });
  const [tag, setTag] = useState('');
  const [projectData, setProjectData] = useState({
    title: '',
    description: '',
    client: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 6)),
    budget: '',
    currency: 'GBP',
    status: 'Planning',
    priority: 'Medium',
    team: [],
    milestones: [],
    risks: [],
    tags: [],
  });

  // Status options
  const statusOptions = ['Planning', 'Active', 'On Hold', 'Completed'];
  const priorityOptions = ['Low', 'Medium', 'High'];
  const severityOptions = ['Low', 'Medium', 'High'];
  const milestoneStatusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold'];
  const currencyOptions = ['GBP', 'USD', 'EUR'];

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Load project data on mount (if only id passed, fetch; else use passed project)
  useEffect(() => {
    const loadProject = async () => {
      setLoading(true);
      let projectId = project?.id || project; // support both object and id
      if (!projectId) {
        Alert.alert('Error', 'No project ID provided');
        navigation.goBack();
        return;
      }

      try {
        // Try to fetch from API first to get latest data
        let fetchedProject = null;
        const endpointsToTry = [
          API_ENDPOINTS.projectById(projectId),
          API_ENDPOINTS.projectByIdFallback(projectId),
        ];

        for (const endpoint of endpointsToTry) {
          try {
            console.log(`Fetching project from: ${endpoint}`);
            const response = await api.get(endpoint);
            if (response.data?.success && response.data.data) {
              fetchedProject = response.data.data;
            } else if (response.data && typeof response.data === 'object') {
              fetchedProject = response.data;
            }
            if (fetchedProject) break;
          } catch (err) {
            console.log(`Fetch failed at ${endpoint}:`, err.message);
          }
        }

        if (!fetchedProject) {
          // Fallback to passed project object if available
          if (project && typeof project === 'object') {
            fetchedProject = project;
          } else {
            Alert.alert('Error', 'Could not load project data');
            navigation.goBack();
            return;
          }
        }

        // Normalize dates and budget
        const normalizedProject = {
          ...fetchedProject,
          title: fetchedProject.name || fetchedProject.title || '',
          description: fetchedProject.description || '',
          client: fetchedProject.client_name || fetchedProject.client_company || '',
          location: fetchedProject.location || '',
          startDate: fetchedProject.start_date ? new Date(fetchedProject.start_date) : new Date(),
          endDate: fetchedProject.deadline ? new Date(fetchedProject.deadline) : new Date(),
          budget: fetchedProject.budget ? String(fetchedProject.budget) : '',
          currency: fetchedProject.currency || 'GBP',
          status: fetchedProject.status || 'Planning',
          priority: fetchedProject.priority || 'Medium',
          team: Array.isArray(fetchedProject.team_members) ? fetchedProject.team_members : [],
          milestones: Array.isArray(fetchedProject.milestones) ? fetchedProject.milestones : [],
          risks: Array.isArray(fetchedProject.risks) ? fetchedProject.risks : [],
          tags: Array.isArray(fetchedProject.tags) ? fetchedProject.tags : [],
        };
        setProjectData(normalizedProject);
      } catch (error) {
        console.error('Error loading project:', error);
        Alert.alert('Error', 'Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [project, navigation]);

  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };

  // Handle date picker
  const showDatePickerModal = field => {
    setCurrentDateField(field);
    setShowDatePicker(true);
  };

  const handleDateSelection = selectedDate => {
    setProjectData({
      ...projectData,
      [currentDateField]: selectedDate,
    });
    setShowDatePicker(false);
  };

  // Handle milestone date selection
  const handleMilestoneDateSelection = selectedDate => {
    setMilestone({
      ...milestone,
      dueDate: selectedDate,
    });
  };

  // Handle team member add
  const handleAddTeamMember = () => {
    if (!teamMember.name || !teamMember.role || teamMember.name.trim() === '' || teamMember.role.trim() === '') {
      Alert.alert('Error', 'Please enter both name and role for the team member');
      return;
    }

    const newTeamMember = {
      ...teamMember,
      id: `t${Date.now()}`,
    };

    setProjectData({
      ...projectData,
      team: [...projectData.team, newTeamMember],
    });

    setTeamMember({
      name: '',
      role: '',
      avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    });
    setShowTeamModal(false);
  };

  // Handle milestone add
  const handleAddMilestone = () => {
    if (!milestone.title || milestone.title.trim() === '') {
      Alert.alert('Error', 'Please enter a title for the milestone');
      return;
    }

    const newMilestone = {
      ...milestone,
      id: `m${Date.now()}`,
    };

    setProjectData({
      ...projectData,
      milestones: [...projectData.milestones, newMilestone],
    });

    setMilestone({ title: '', dueDate: new Date(), status: 'Not Started' });
    setShowMilestoneModal(false);
  };

  // Handle risk add
  const handleAddRisk = () => {
    if (!risk.description || !risk.mitigation || risk.description.trim() === '' || risk.mitigation.trim() === '') {
      Alert.alert('Error', 'Please enter both description and mitigation for the risk');
      return;
    }

    const newRisk = {
      ...risk,
      id: `r${Date.now()}`,
    };

    setProjectData({
      ...projectData,
      risks: [...projectData.risks, newRisk],
    });

    setRisk({ description: '', severity: 'Medium', mitigation: '' });
    setShowRiskModal(false);
  };

  // Handle tag add
  const handleAddTag = () => {
    if (!tag || tag.trim() === '') {
      Alert.alert('Error', 'Please enter a tag');
      return;
    }

    if (projectData.tags.includes(tag.trim())) {
      Alert.alert('Error', 'This tag already exists');
      return;
    }

    setProjectData({
      ...projectData,
      tags: [...projectData.tags, tag.trim()],
    });

    setTag('');
    setShowTagModal(false);
  };

  // Handle remove team member
  const handleRemoveTeamMember = id => {
    setProjectData({
      ...projectData,
      team: projectData.team.filter(member => member.id !== id),
    });
  };

  // Handle remove milestone
  const handleRemoveMilestone = id => {
    setProjectData({
      ...projectData,
      milestones: projectData.milestones.filter(item => item.id !== id),
    });
  };

  // Handle remove risk
  const handleRemoveRisk = id => {
    setProjectData({
      ...projectData,
      risks: projectData.risks.filter(item => item.id !== id),
    });
  };

  // Handle remove tag
  const handleRemoveTag = tag => {
    setProjectData({
      ...projectData,
      tags: projectData.tags.filter(item => item !== tag),
    });
  };

  // Format date
  const formatDate = date => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount, currencyCode = 'GBP') => {
    if (!amount) return '';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount;
    if (isNaN(numericAmount)) return '';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
    }).format(numericAmount);
  };

  // Get status color
  const getStatusColor = status => {
    switch (status) {
      case 'Active': return '#10B981';
      case 'Planning': return '#8B5CF6';
      case 'On Hold': return '#F59E0B';
      case 'Completed': return '#0e4e96';
      default: return '#64748B';
    }
  };

  // Get priority color
  const getPriorityColor = priority => {
    switch (priority) {
      case 'High': return '#EF4444';
      case 'Medium': return '#F59E0B';
      case 'Low': return '#10B981';
      default: return '#64748B';
    }
  };

  // Get severity color
  const getSeverityColor = severity => {
    switch (severity) {
      case 'High': return '#EF4444';
      case 'Medium': return '#F59E0B';
      case 'Low': return '#10B981';
      default: return '#64748B';
    }
  };

  // ==================== API SYNC ====================
  const syncProjectToAPI = async (updatedProject) => {
    if (!updatedProject || !updatedProject.id) {
      console.error('Cannot sync: missing project ID');
      return false;
    }

    setIsSaving(true);
    let success = false;

    // Fetch current project to get all fields (in case we're missing some)
    let currentProject = null;
    const fetchEndpoints = [
      API_ENDPOINTS.projectById(updatedProject.id),
      API_ENDPOINTS.projectByIdFallback(updatedProject.id),
    ];

    for (const endpoint of fetchEndpoints) {
      try {
        console.log(`Fetching current project from: ${endpoint}`);
        const response = await api.get(endpoint);
        if (response.data?.success && response.data.data) {
          currentProject = response.data.data;
        } else if (response.data && typeof response.data === 'object') {
          currentProject = response.data;
        }
        if (currentProject) break;
      } catch (error) {
        console.log(`Fetch failed at ${endpoint}:`, error.message);
      }
    }

    if (!currentProject) {
      Alert.alert('Error', 'Could not retrieve current project data. Please try again.');
      setIsSaving(false);
      return false;
    }

    // Merge updated fields
    const mergedProject = {
      ...currentProject,
      name: updatedProject.title,
      description: updatedProject.description,
      client_name: updatedProject.client,
      location: updatedProject.location,
      start_date: updatedProject.startDate.toISOString(),
      deadline: updatedProject.endDate.toISOString(),
      budget: parseFloat(updatedProject.budget),
      currency: updatedProject.currency,
      status: String(updatedProject.status ?? '').toLowerCase(),
      priority: String(updatedProject.priority ?? '').toLowerCase(),
      team_members: updatedProject.team.map(member => ({
        id: member.id,
        name: member.name,
        role: member.role,
        avatar: member.avatar,
      })),
      milestones: updatedProject.milestones.map(m => ({
        ...m,
        dueDate: m.dueDate instanceof Date ? m.dueDate.toISOString() : m.dueDate,
      })),
      risks: updatedProject.risks,
      tags: updatedProject.tags,
    };

    // Remove any UI-only fields that might cause issues
    delete mergedProject.team;
    delete mergedProject.recentUpdates;
    delete mergedProject.objectives;
    delete mergedProject.scope;
    delete mergedProject.deliverables;
    delete mergedProject.assumptions;
    delete mergedProject.constraints;

    const updateEndpoints = [
      API_ENDPOINTS.projectById(updatedProject.id),
      API_ENDPOINTS.projectByIdFallback(updatedProject.id),
    ];

    for (const endpoint of updateEndpoints) {
      try {
        console.log(`Attempting to update project at: ${endpoint}`);
        const response = await api.put(endpoint, mergedProject);

        if (response.status === 200 || response.status === 204 || response.data?.success) {
          console.log(`✅ Project updated successfully at ${endpoint}`);
          success = true;
          break;
        } else {
          console.warn(`Update failed at ${endpoint}: unexpected response`, response.data);
        }
      } catch (error) {
        console.log(`❌ Update failed at ${endpoint}:`, error.message);
        if (error.response) {
          console.log('Error response data:', error.response.data);
        }
      }
    }

    if (!success) {
      Alert.alert('Error', 'Failed to sync changes to server. Please try again.');
    }
    setIsSaving(false);
    return success;
  };

  // Handle update project
  const handleUpdateProject = async () => {
    // Validate required fields
    if (
      !projectData.title ||
      !projectData.client ||
      !projectData.location ||
      !projectData.budget ||
      !projectData.description ||
      projectData.title.trim() === '' ||
      projectData.client.trim() === '' ||
      projectData.location.trim() === '' ||
      projectData.description.trim() === ''
    ) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    // Validate budget is a number
    const budgetValue = parseFloat(projectData.budget.replace(/[^0-9.]/g, ''));
    if (isNaN(budgetValue) || budgetValue <= 0) {
      Alert.alert('Error', 'Please enter a valid budget amount');
      return;
    }

    // Prepare the project data for saving (optimistic update already done)
    const updatedProject = {
      ...projectData,
      budget: String(budgetValue),
      startDate: projectData.startDate,
      endDate: projectData.endDate,
    };

    const synced = await syncProjectToAPI(updatedProject);
    if (synced) {
      Alert.alert('Success', 'Project has been updated successfully', [
        {
          text: 'OK',
          onPress: () => navigation.navigate('ProjectDetails', { projectId: projectData.id }),
        },
      ]);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading project...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header with Back Button, Company Logo, and Title */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Edit Project</Text>
        </View>

        <TouchableOpacity style={styles.headerButton}>
          <Ionicons name="help-circle-outline" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {/* Project Basic Information */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Basic Information</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Project Title *</Text>
              <TextInput
                style={styles.input}
                value={projectData.title}
                onChangeText={text => setProjectData({ ...projectData, title: text })}
                placeholder="Enter project title"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Client *</Text>
              <TextInput
                style={styles.input}
                value={projectData.client}
                onChangeText={text => setProjectData({ ...projectData, client: text })}
                placeholder="Enter client name"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Location *</Text>
              <TextInput
                style={styles.input}
                value={projectData.location}
                onChangeText={text => setProjectData({ ...projectData, location: text })}
                placeholder="Enter project location"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Description *</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={projectData.description}
                onChangeText={text => setProjectData({ ...projectData, description: text })}
                placeholder="Enter project description"
                placeholderTextColor="#94A3B8"
                multiline={true}
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>
          </View>

          {/* Project Timeline */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Timeline</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Start Date</Text>
              <TouchableOpacity style={styles.dateInput} onPress={() => showDatePickerModal('startDate')}>
                <Text style={styles.dateText}>{formatDate(projectData.startDate)}</Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>End Date</Text>
              <TouchableOpacity style={styles.dateInput} onPress={() => showDatePickerModal('endDate')}>
                <Text style={styles.dateText}>{formatDate(projectData.endDate)}</Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Project Budget */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Budget</Text>

            <View style={styles.formRow}>
              <View style={[styles.formGroup, { flex: 2, marginRight: 10 }]}>
                <Text style={styles.label}>Budget Amount *</Text>
                <TextInput
                  style={styles.input}
                  value={projectData.budget}
                  onChangeText={text => {
                    const sanitizedText = text.replace(/[^0-9.]/g, '');
                    setProjectData({ ...projectData, budget: sanitizedText });
                  }}
                  placeholder="Enter budget amount"
                  placeholderTextColor="#94A3B8"
                  keyboardType="numeric"
                />
              </View>

              <View style={[styles.formGroup, { flex: 1 }]}>
                <Text style={styles.label}>Currency</Text>
                <View style={styles.pickerContainer}>
                  {currencyOptions.map(currency => (
                    <TouchableOpacity
                      key={currency}
                      style={[
                        styles.pickerItem,
                        projectData.currency === currency && styles.pickerItemSelected,
                      ]}
                      onPress={() => setProjectData({ ...projectData, currency })}
                    >
                      <Text
                        style={[
                          styles.pickerItemText,
                          projectData.currency === currency && styles.pickerItemTextSelected,
                        ]}
                      >
                        {currency}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>

            {projectData.budget ? (
              <Text style={styles.formattedBudget}>
                {formatCurrency(projectData.budget, projectData.currency)}
              </Text>
            ) : null}
          </View>

          {/* Project Status and Priority */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Status & Priority</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Status</Text>
              <View style={styles.pickerContainer}>
                {statusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.pickerItem,
                      projectData.status === status && styles.pickerItemSelected,
                      projectData.status === status && { borderColor: getStatusColor(status) },
                    ]}
                    onPress={() => setProjectData({ ...projectData, status })}
                  >
                    <View style={[styles.statusIndicator, { backgroundColor: getStatusColor(status) }]} />
                    <Text
                      style={[
                        styles.pickerItemText,
                        projectData.status === status && styles.pickerItemTextSelected,
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Priority</Text>
              <View style={styles.pickerContainer}>
                {priorityOptions.map(priority => (
                  <TouchableOpacity
                    key={priority}
                    style={[
                      styles.pickerItem,
                      projectData.priority === priority && styles.pickerItemSelected,
                      projectData.priority === priority && { borderColor: getPriorityColor(priority) },
                    ]}
                    onPress={() => setProjectData({ ...projectData, priority })}
                  >
                    <View
                      style={[styles.priorityIndicator, { backgroundColor: getPriorityColor(priority) }]}
                    />
                    <Text
                      style={[
                        styles.pickerItemText,
                        projectData.priority === priority && styles.pickerItemTextSelected,
                      ]}
                    >
                      {priority}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Project Team */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Team Members</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowTeamModal(true)}>
                <Ionicons name="add" size={18} color="#fff" />
                <Text style={styles.addButtonText}>Add Member</Text>
              </TouchableOpacity>
            </View>

            {projectData.team.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="account-group" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No team members added yet</Text>
              </View>
            ) : (
              <View style={styles.teamList}>
                {projectData.team.map(member => (
                  <View key={member.id} style={styles.teamMember}>
                    <Image source={{ uri: (member.avatar && member.avatar.startsWith('http')) ? member.avatar : undefined }} style={styles.avatar} />
                    <View style={styles.memberInfo}>
                      <Text style={styles.memberName}>{member.name}</Text>
                      <Text style={styles.memberRole}>{member.role}</Text>
                    </View>
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => handleRemoveTeamMember(member.id)}
                    >
                      <Ionicons name="close-circle" size={22} color="#64748B" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Project Milestones */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Milestones</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowMilestoneModal(true)}>
                <Ionicons name="add" size={18} color="#fff" />
                <Text style={styles.addButtonText}>Add Milestone</Text>
              </TouchableOpacity>
            </View>

            {projectData.milestones.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="flag-checkered" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No milestones added yet</Text>
              </View>
            ) : (
              <View style={styles.milestoneList}>
                {projectData.milestones.map(item => (
                  <View key={item.id} style={styles.milestone}>
                    <View style={styles.milestoneHeader}>
                      <Text style={styles.milestoneTitle}>{item.title}</Text>
                      <TouchableOpacity
                        style={styles.removeButton}
                        onPress={() => handleRemoveMilestone(item.id)}
                      >
                        <Ionicons name="close-circle" size={22} color="#64748B" />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.milestoneDetails}>
                      <View style={styles.milestoneDetail}>
                        <Ionicons name="calendar-outline" size={16} color="#64748B" />
                        <Text style={styles.milestoneDetailText}>
                          {item.dueDate instanceof Date
                            ? formatDate(item.dueDate)
                            : formatDate(new Date(item.dueDate))}
                        </Text>
                      </View>
                      <View style={[styles.milestoneStatus, { backgroundColor: getStatusColor(item.status) }]}>
                        <Text style={styles.milestoneStatusText}>{item.status}</Text>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Project Risks */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Risk Assessment</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowRiskModal(true)}>
                <Ionicons name="add" size={18} color="#fff" />
                <Text style={styles.addButtonText}>Add Risk</Text>
              </TouchableOpacity>
            </View>

            {projectData.risks.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="alert-circle-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No risks added yet</Text>
              </View>
            ) : (
              <View style={styles.riskList}>
                {projectData.risks.map(item => (
                  <View key={item.id} style={styles.risk}>
                    <View style={styles.riskHeader}>
                      <View style={[styles.riskSeverity, { backgroundColor: getSeverityColor(item.severity) }]}>
                        <Text style={styles.riskSeverityText}>{item.severity}</Text>
                      </View>
                      <TouchableOpacity
                        style={styles.removeButton}
                        onPress={() => handleRemoveRisk(item.id)}
                      >
                        <Ionicons name="close-circle" size={22} color="#64748B" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.riskDescription}>{item.description}</Text>
                    <View style={styles.riskMitigation}>
                      <Text style={styles.riskMitigationLabel}>Mitigation:</Text>
                      <Text style={styles.riskMitigationText}>{item.mitigation}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Project Tags */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Tags</Text>
              <TouchableOpacity style={styles.addButton} onPress={() => setShowTagModal(true)}>
                <Ionicons name="add" size={18} color="#fff" />
                <Text style={styles.addButtonText}>Add Tag</Text>
              </TouchableOpacity>
            </View>

            {projectData.tags.length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="tag-outline" size={40} color="#CBD5E1" />
                <Text style={styles.emptyStateText}>No tags added yet</Text>
              </View>
            ) : (
              <View style={styles.tagList}>
                {projectData.tags.map((tag, index) => (
                  <View key={index} style={styles.tag}>
                    <Text style={styles.tagText}>{tag}</Text>
                    <TouchableOpacity style={styles.removeTagButton} onPress={() => handleRemoveTag(tag)}>
                      <Ionicons name="close" size={16} color="#64748B" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Loading Overlay */}
          {isSaving && (
            <View style={styles.savingOverlay}>
              <ActivityIndicator size={36} color="#0e4e96" />
              <Text style={styles.savingText}>Saving changes...</Text>
            </View>
          )}

          {/* Update Button */}
          <TouchableOpacity style={styles.updateButton} onPress={handleUpdateProject} disabled={isSaving}>
            {isSaving ? (
              <ActivityIndicator size={20} color="#fff" />
            ) : (
              <>
                <Ionicons name="save-outline" size={20} color="#fff" />
                <Text style={styles.updateButtonText}>Update Project</Text>
              </>
            )}
          </TouchableOpacity>

          <View style={styles.footer}>
            <Text style={styles.footerText}>* Required fields</Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Date Picker Modal */}
      <CustomDatePicker
        isVisible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelectDate={handleDateSelection}
        initialDate={currentDateField ? projectData[currentDateField] : new Date()}
      />

      {/* Add Team Member Modal */}
      <Modal animationType="slide" transparent={true} visible={showTeamModal} onRequestClose={() => setShowTeamModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Team Member</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowTeamModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Name</Text>
              <TextInput
                style={styles.input}
                value={teamMember.name}
                onChangeText={text => setTeamMember({ ...teamMember, name: text })}
                placeholder="Enter team member name"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Role</Text>
              <TextInput
                style={styles.input}
                value={teamMember.role}
                onChangeText={text => setTeamMember({ ...teamMember, role: text })}
                placeholder="Enter team member role"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddTeamMember}>
              <Text style={styles.modalActionButtonText}>Add Team Member</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Milestone Modal */}
      <Modal animationType="slide" transparent={true} visible={showMilestoneModal} onRequestClose={() => setShowMilestoneModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Milestone</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowMilestoneModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Title</Text>
              <TextInput
                style={styles.input}
                value={milestone.title}
                onChangeText={text => setMilestone({ ...milestone, title: text })}
                placeholder="Enter milestone title"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Due Date</Text>
              <TouchableOpacity
                style={styles.dateInput}
                onPress={() => {
                  setCurrentDateField('milestoneDueDate');
                  setShowDatePicker(true);
                }}
              >
                <Text style={styles.dateText}>{formatDate(milestone.dueDate)}</Text>
                <Ionicons name="calendar-outline" size={20} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Status</Text>
              <View style={styles.pickerContainer}>
                {milestoneStatusOptions.map(status => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.pickerItem,
                      milestone.status === status && styles.pickerItemSelected,
                      milestone.status === status && { borderColor: getStatusColor(status) },
                    ]}
                    onPress={() => setMilestone({ ...milestone, status })}
                  >
                    <View style={[styles.statusIndicator, { backgroundColor: getStatusColor(status) }]} />
                    <Text
                      style={[
                        styles.pickerItemText,
                        milestone.status === status && styles.pickerItemTextSelected,
                      ]}
                    >
                      {status}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddMilestone}>
              <Text style={styles.modalActionButtonText}>Add Milestone</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Risk Modal */}
      <Modal animationType="slide" transparent={true} visible={showRiskModal} onRequestClose={() => setShowRiskModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Risk</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowRiskModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={risk.description}
                onChangeText={text => setRisk({ ...risk, description: text })}
                placeholder="Enter risk description"
                placeholderTextColor="#94A3B8"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Severity</Text>
              <View style={styles.pickerContainer}>
                {severityOptions.map(severity => (
                  <TouchableOpacity
                    key={severity}
                    style={[
                      styles.pickerItem,
                      risk.severity === severity && styles.pickerItemSelected,
                      risk.severity === severity && { borderColor: getSeverityColor(severity) },
                    ]}
                    onPress={() => setRisk({ ...risk, severity })}
                  >
                    <View style={[styles.severityIndicator, { backgroundColor: getSeverityColor(severity) }]} />
                    <Text
                      style={[
                        styles.pickerItemText,
                        risk.severity === severity && styles.pickerItemTextSelected,
                      ]}
                    >
                      {severity}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Mitigation</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={risk.mitigation}
                onChangeText={text => setRisk({ ...risk, mitigation: text })}
                placeholder="Enter risk mitigation strategy"
                placeholderTextColor="#94A3B8"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddRisk}>
              <Text style={styles.modalActionButtonText}>Add Risk</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Tag Modal */}
      <Modal animationType="slide" transparent={true} visible={showTagModal} onRequestClose={() => setShowTagModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.smallModalContent]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Tag</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowTagModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Tag Name</Text>
              <TextInput
                style={styles.input}
                value={tag}
                onChangeText={setTag}
                placeholder="Enter tag name"
                placeholderTextColor="#94A3B8"
              />
            </View>

            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddTag}>
              <Text style={styles.modalActionButtonText}>Add Tag</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  headerButton: {
    padding: 5,
  },
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  formRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  textArea: {
    minHeight: 100,
    paddingTop: 12,
  },
  dateInput: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  dateText: {
    fontSize: 16,
    color: '#0f172a',
  },
  pickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginRight: 8,
    marginBottom: 8,
  },
  pickerItemSelected: {
    backgroundColor: '#f0f9ff',
    borderWidth: 1,
  },
  pickerItemText: {
    fontSize: 14,
    color: '#64748B',
  },
  pickerItemTextSelected: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  priorityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  severityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  formattedBudget: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginTop: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 8,
  },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
  },
  teamList: {
    marginTop: 8,
  },
  teamMember: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  memberInfo: {
    flex: 1,
    marginLeft: 12,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0f172a',
  },
  memberRole: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 2,
  },
  removeButton: {
    padding: 4,
  },
  milestoneList: {
    marginTop: 8,
  },
  milestone: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0f172a',
    flex: 1,
  },
  milestoneDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  milestoneDetail: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  milestoneDetailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 6,
  },
  milestoneStatus: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  milestoneStatusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  riskList: {
    marginTop: 8,
  },
  risk: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  riskHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  riskSeverity: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  riskSeverityText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  riskDescription: {
    fontSize: 15,
    color: '#0f172a',
    marginBottom: 8,
  },
  riskMitigation: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  riskMitigationLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginRight: 6,
  },
  riskMitigationText: {
    fontSize: 14,
    color: '#64748B',
    flex: 1,
  },
  tagList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    fontSize: 14,
    color: '#64748B',
    marginRight: 4,
  },
  removeTagButton: {
    padding: 2,
  },
  savingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  savingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#0e4e96',
    fontWeight: '600',
  },
  updateButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    flexDirection: 'row',
  },
  updateButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  footer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  footerText: {
    fontSize: 12,
    color: '#64748B',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    maxHeight: '80%',
  },
  smallModalContent: {
    maxHeight: '40%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalActionButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  modalActionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
});